import { Component, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { from, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment';

import { LicenseService } from '../../core/services/http-services/license.service';
import { LicenseStatusModel } from '../../core/models/view-models/license-models/license-status.model';
import { AppConstants } from '../../app.constants';
import { LicenseInfoModel } from '../../core/models/view-models/license-models/license-info.model';
import { ModalInstallLicenseComponent } from '../../admin-module/modal-install-license/modal-install-license.component';
import { RemindMeEnum } from '../../core/models/enums/remind-me.enum';

@Component({
  selector: 'app-evaluation-period-notification',
  templateUrl: './evaluation-period-notification.component.html',
  styleUrls: ['../evaluation-period-expired/evaluation-period-expired.component.scss'],
  providers: [DatePipe]
})
export class EvaluationPeriodNotificationComponent implements OnDestroy {

  public isToShowNotification: boolean = false;
  public isToShowUpgradeNotification: boolean = false;
  public supportExpirationDate: string = null;
  public licenseStatus$: Subject<LicenseStatusModel> = new Subject();
  public licenseInfo$: Subject<LicenseInfoModel> = new Subject();
  public supportExpirationMonthPast: number;
  public amountOfDays: RemindMeEnum[] = Object.values(RemindMeEnum);
  private licenseSupportDaysLeft: number;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly licenseService: LicenseService,
    private readonly datePipe: DatePipe,
    private readonly dialog: MatDialog,
    private readonly router: Router,
  ) {
    this.licenseStatus$ = this.licenseService.licenseStatusModel$;
    this.licenseInfo$ = this.licenseService.licenseInfoModel;
    this.subscriptions.add(
      this.licenseService.isShowUserLicenseNotification.subscribe((isToShow: boolean) => {
        this.isToShowNotification = isToShow;
      })
    );

    this.subscriptions.add(
      this.licenseService.isShowUserUpgradeLicenseNotification.subscribe((isToShow: boolean) => {
        this.isToShowUpgradeNotification = isToShow;
      })
    );

    this.subscriptions.add(
      this.licenseService.licenseStatusModel$.subscribe((licenseModel: LicenseStatusModel) => {
        if (licenseModel && licenseModel.IsLicensed && licenseModel.Status) {
          this.licenseSupportDaysLeft = licenseModel.LicenseSupportDaysLeft;
          this.getExpirationDate(licenseModel);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  openActivationLicenseDialog(): void {
    from(this.router.navigateByUrl('/Admin/License')).pipe(
      take(1)
    ).subscribe(() => {
      this.dialog.open(ModalInstallLicenseComponent, {
        width: AppConstants.MODAL_WINDOW_WIDTH,
        disableClose: true,
        panelClass: 'custom-mat-dialog-class'
      });
    });
  }

  onClose(daysToDelay?: number): void {
    if (this.isToShowUpgradeNotification) {
      this.licenseService.isShowUserUpgradeLicenseNotification.next(false);
    }
    else {
      this.licenseService.isShowUserLicenseNotification.next(false);
      localStorage.setItem(AppConstants.IS_NOTIFICATION_WINDOW_CLOSED, `${true}`);

      this.licenseSupportDaysLeft > 0
        ? localStorage.setItem(AppConstants.NOTIFICATION_LAST_DATE_CLOSED, new Date().toString())
        : localStorage.removeItem(AppConstants.NOTIFICATION_LAST_DATE_CLOSED);

      if (daysToDelay) {
        const showLicenseNotificationDate = moment(new Date()).add(daysToDelay, 'days').format('YYYY/MM/D');
        localStorage.setItem(AppConstants.NOTIFICATION_SHOW_DATE, showLicenseNotificationDate.toString());
      }
    }
  }

  private getEndDateValue(licenseInfo: LicenseInfoModel): string {
    if (licenseInfo) {
      const dateValue: Date = licenseInfo.PreviousSupportExpirationDate;

      if (dateValue) {
        return this.datePipe.transform(dateValue, AppConstants.DEFAULT_DATE_TIME_FORMAT);
      } else if (dateValue === null) {
        return AppConstants.PERMANENT_LICENSE_TYPE;
      }
    }

    return null;
  }

  private getExpirationDate(licenseModel: LicenseStatusModel): void {
    this.subscriptions.add(
      this.licenseService.licenseInfoModel.subscribe((licenseInfo: LicenseInfoModel) => {

        if (licenseInfo) {
          this.supportExpirationDate = this.getEndDateValue(licenseInfo);
          this.supportExpirationMonthPast = licenseModel.IsVersionUpgrade
            ? this.getMonthPast(licenseInfo.PreviousSupportExpirationDate)
            : this.getMonthPast(licenseInfo.SupportExpirationDate);
        }
      })
    );
  }

  private getMonthPast(date: Date) {
    return date ? moment().diff(moment(new Date(date)), 'months', true) : 0;
  }
}
